import { Link } from "wouter";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { ContactForm } from "@/components/ContactForm";
import {
  Phone,
  CalendarCheck,
  MessageSquare,
  ArrowRight,
  Wrench,
  Wind,
  Zap,
  ClipboardList,
  Sun,
  Building2,
  CheckCircle,
  Star,
  Shield,
  Users,
  CreditCard,
  Flame,
} from "lucide-react";

const heroHome = "/assets/hero-bg.webp";
import photonSystemImg from "@assets/Photon_Unit_system_1785783901843.webp";
import photonSystemImg640 from "@assets/Photon_Unit_system_1785783901843_640w.webp";
import job1 from "@assets/13514cd3-1a49-4ed3-938b-e178dda3a82a_1786127354980.JPG";
import job2 from "@assets/D50DF686-ADB5-4798-AA06-55D5728A3424_1786127354980.JPG";
import job3 from "@assets/IMG_0166_1786127361555.jpeg";
import job4 from "@assets/IMG_3871_1786127361556.jpeg";
import job5 from "@assets/IMG_5521_1786127361556.jpeg";
import job6 from "@assets/IMG_0549_1786141493486.jpeg";
import job7 from "@assets/IMG_1082_1786141493487.jpeg";
import job8 from "@assets/IMG_1108_1786141493487.jpeg";
import job9 from "@assets/IMG_2558_1786141493487.jpeg";
import job10 from "@assets/IMG_2835_1786141493487.jpeg";
import job11 from "@assets/IMG_3149_1786141493487.jpeg";
import job12 from "@assets/IMG_3865_1786141493488.jpeg";
import job13 from "@assets/IMG_6430_1786141493488.jpeg";
import job14 from "@assets/IMG_7646_1786141493488.jpeg";
import job15 from "@assets/IMG_9368_1786141493488.jpeg";
import dealerBadge from "@assets/Authorized_photon_ac_dealer_badge__1767989518673.webp";

const jobPhotos = [
  { src: job1, alt: "Ductless mini-split condenser installed on the side of an Arizona home by Caribbean Breeze HVAC" },
  { src: job2, alt: "Caribbean Breeze HVAC service van on a job in the West Valley, Arizona" },
  { src: job3, alt: "Caribbean Breeze HVAC van at a commercial job site — Feel the Breeze" },
  { src: job4, alt: "Caribbean Breeze HVAC van parked at a residential AC job in Surprise, AZ" },
  { src: job5, alt: "Completed HVAC installation by Caribbean Breeze — Surprise, AZ" },
  { src: job6, alt: "Mini-split condenser unit installed on the exterior of a new modular tiny home" },
  { src: job7, alt: "Indoor mini-split air handler mounted in a modern wood-panel interior — Caribbean Breeze HVAC" },
  { src: job8, alt: "Commercial HVAC installation with crane at a multi-story building — Caribbean Breeze HVAC" },
  { src: job9, alt: "Dual Bosch air handlers installed in a utility room — Caribbean Breeze HVAC" },
  { src: job10, alt: "Goodman rooftop package unit with custom duct transition on a commercial building" },
  { src: job11, alt: "HVAC job completed by Caribbean Breeze HVAC in the West Valley, Arizona" },
  { src: job12, alt: "HVAC installation by Caribbean Breeze HVAC — Surprise, AZ" },
  { src: job13, alt: "AC system installed by Caribbean Breeze HVAC in the Phoenix metro area" },
  { src: job14, alt: "HVAC equipment installed by Caribbean Breeze HVAC — West Valley, Arizona" },
  { src: job15, alt: "Completed heating and cooling job by Caribbean Breeze HVAC — Surprise, AZ" },
];

const galleryImageSchema = {
  "@context": "https://schema.org",
  "@type": "ImageGallery",
  name: "Caribbean Breeze HVAC — Our Work",
  description:
    "Real photos of AC installation, repair, ductless mini-split, and commercial HVAC jobs completed by Caribbean Breeze HVAC in Surprise and the West Valley, Arizona.",
  image: jobPhotos.map((p) => ({
    "@type": "ImageObject",
    contentUrl: `https://TODO-DOMAIN.com${p.src}`,
    description: p.alt,
    creditText: "Caribbean Breeze HVAC",
  })),
};

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "HVACBusiness",
  "@id": "https://TODO-DOMAIN.com/#localbusiness",
  name: "Caribbean Breeze HVAC",
  alternateName: "Caribbean Breeze Indoor Comfort Air Conditioning & Heating",
  url: "https://TODO-DOMAIN.com",
  telephone: "+16027770278",
  email: "caribbeanbreezeaz@gmail.com",
  foundingDate: "2017",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Surprise",
    addressRegion: "AZ",
    postalCode: "85378",
    addressCountry: "US",
  },
  areaServed: [
    "Surprise", "Peoria", "Glendale", "Phoenix",
    "Avondale", "Goodyear", "Buckeye", "Litchfield Park",
  ],
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday"],
    opens: "08:00",
    closes: "17:00",
  },
  paymentAccepted: "Cash, Credit Card, PayPal, Zelle",
};

const services = [
  {
    icon: Wrench,
    title: "AC & Heating Installation",
    desc: "New system or full replacement — properly sized, correctly installed, and backed by a 2-year labor warranty.",
    bullets: ["Goodman & Bosch systems", "Proper Manual-J sizing", "2-year labor warranty"],
    href: "/ac-heating-installation",
    accent: "blue",
  },
  {
    icon: Wind,
    title: "AC & Heating Repair",
    desc: "Something's wrong with your system? We diagnose it fast, tell you what it needs, and fix it right.",
    bullets: ["All makes & models", "Emergency service available", "Straight-up diagnosis"],
    href: "/ac-heating-repair",
    accent: "red",
  },
  {
    icon: Zap,
    title: "Ductless Mini-Splits",
    desc: "Add comfort to any room, garage, or ADU without running new ductwork. Quiet, efficient, and flexible.",
    bullets: ["Residential & commercial", "Single & multi-zone", "Solar-ready options"],
    href: "/ductless-mini-splits",
    accent: "teal",
  },
  {
    icon: Building2,
    title: "Commercial HVAC",
    desc: "Rooftop units, package systems, and light commercial cooling for offices, retail, and small businesses.",
    bullets: ["Rooftop unit replacement", "Package systems", "Light commercial service"],
    href: "/commercial-hvac",
    accent: "slate",
  },
  {
    icon: ClipboardList,
    title: "Maintenance Plans",
    desc: "An annual tune-up keeps your system running longer, catches small problems before they get expensive.",
    bullets: ["Spring & fall check-ups", "Filter & coil service", "Priority scheduling"],
    href: "/maintenance-plans",
    accent: "green",
  },
  {
    icon: Sun,
    title: "Solar-Powered AC",
    desc: "Run your AC on sunlight during the day. Zero battery needed — your panels power the system directly.",
    bullets: ["Authorized Photon AC dealer", "No batteries required", "Daytime cost → near $0"],
    href: "/solar-powered-ac",
    accent: "amber",
  },
];

const accentMap: Record<string, { icon: string; card: string; bullet: string }> = {
  blue:  { icon: "text-blue-600",  card: "border-t-blue-500",  bullet: "bg-blue-100 text-blue-700" },
  red:   { icon: "text-red-600",   card: "border-t-red-500",   bullet: "bg-red-100 text-red-700" },
  teal:  { icon: "text-teal-600",  card: "border-t-teal-500",  bullet: "bg-teal-100 text-teal-700" },
  slate: { icon: "text-slate-600", card: "border-t-slate-500", bullet: "bg-slate-100 text-slate-700" },
  green: { icon: "text-green-600", card: "border-t-green-500", bullet: "bg-green-100 text-green-700" },
  amber: { icon: "text-amber-600", card: "border-t-amber-500", bullet: "bg-amber-100 text-amber-700" },
};

const trustBadges = [
  { icon: Users,       label: "Family Owned & Operated" },
  { icon: Star,        label: "Owner-Operator Since 2017" },
  { icon: Shield,      label: "2-Year Labor Warranty" },
  { icon: CheckCircle, label: "Emergency Service Available" },
];

export default function Home() {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [autoplayPaused, setAutoplayPaused] = useState(false);

  useEffect(() => {
    if (!carouselApi || autoplayPaused) return;
    const interval = setInterval(() => {
      carouselApi.scrollNext();
    }, 1400);
    return () => clearInterval(interval);
  }, [carouselApi, autoplayPaused]);
  return (
    <>
      <Helmet>
        <title>Caribbean Breeze HVAC | Surprise &amp; West Valley, AZ | Heating &amp; Cooling</title>
        <meta
          name="description"
          content="Caribbean Breeze HVAC — Surprise, AZ. AC &amp; heating installation, repair, mini-splits, commercial HVAC, maintenance, and solar-powered AC. Family owned since 2017. Call (602) 777-0278."
        />
        <link rel="canonical" href="https://TODO-DOMAIN.com/" />
        <meta property="og:title" content="Caribbean Breeze HVAC | Surprise &amp; West Valley, AZ" />
        <meta property="og:description" content="Honest heating &amp; cooling service in Surprise, AZ. Installation, repair, mini-splits, commercial HVAC. Family owned since 2017." />
        <meta property="og:url" content="https://TODO-DOMAIN.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://TODO-DOMAIN.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(galleryImageSchema)}</script>
      </Helmet>

      <div className="min-h-screen">

        {/* ─── HERO ──────────────────────────────────────────── */}
        <section
          className="relative min-h-[380px] sm:min-h-[540px] lg:min-h-[660px] flex items-center"
          data-testid="section-hero"
        >
          <img
            src={heroHome}
            alt="Phoenix area home with new HVAC system installed"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            // @ts-ignore
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-black/75" />
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white py-12 sm:py-24">
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-sky-300 mb-3">
              Surprise &amp; West Valley, Arizona
            </p>
            <h1
              className="font-bold leading-tight mb-4 sm:mb-6 text-3xl sm:text-5xl lg:text-6xl"
              data-testid="text-hero-headline"
            >
              Feel the Breeze.
              <br className="hidden sm:block" />
              <span className="text-amber-300">Stay Comfortable Year-Round.</span>
            </h1>
            <div className="inline-block max-w-2xl mx-auto rounded-xl bg-black/40 backdrop-blur-sm px-5 py-4 mb-7 sm:mb-9">
              <p className="text-sm sm:text-lg text-white/95 mb-2">
                AC &amp; heating installation, repair, mini-splits, commercial HVAC, and
                solar-powered cooling — done right by a family-owned West Valley company.
              </p>
              <p className="text-xs sm:text-sm text-white/75">
                Owner-operated · 2-year labor warranty · Emergency service available
              </p>
            </div>
            <div className="flex flex-row flex-wrap items-center justify-center gap-3">
              <a href="tel:6027770278" data-testid="button-hero-call">
                <Button
                  size="sm"
                  className="bg-amber-400 hover:bg-amber-500 text-black font-bold px-5 sm:px-8 sm:h-12 sm:text-base"
                >
                  <Phone className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                  (602) 777-0278
                </Button>
              </a>
              <a href="sms:6027770278" data-testid="button-hero-text">
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20 font-semibold px-5 sm:px-8 sm:h-12 sm:text-base"
                >
                  <MessageSquare className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                  Text Us
                </Button>
              </a>
              <Link href="/contact" data-testid="button-hero-schedule">
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20 font-semibold px-5 sm:px-8 sm:h-12 sm:text-base"
                >
                  <CalendarCheck className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                  Free Estimate
                </Button>
              </Link>
            </div>
            <p className="mt-5 text-xs sm:text-sm font-semibold text-amber-300">
              Financing available — new AC as low as $89/mo.*{" "}
              <Link href="/financing" className="underline underline-offset-2 hover:text-amber-200">
                See financing options
              </Link>
            </p>
          </div>
        </section>

        {/* ─── TRUST STRIP ───────────────────────────────────── */}
        <section className="bg-primary text-primary-foreground py-4" data-testid="section-trust-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs sm:text-sm font-medium">
              {trustBadges.map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.label} className="flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
                    <span>{b.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ─── SERVICES GRID ─────────────────────────────────── */}
        <section className="py-10 sm:py-16" data-testid="section-services">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">What We Do</p>
              <h2 className="text-2xl sm:text-3xl font-bold">Pick Your Service</h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
              {services.map((s) => {
                const Icon = s.icon;
                const colors = accentMap[s.accent];
                return (
                  <Link key={s.title} href={s.href}>
                    <div className={`group flex items-center gap-3 rounded-xl border bg-card px-4 py-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 cursor-pointer`}>
                      <div className={`w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${colors.icon}`} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm leading-tight group-hover:text-primary transition-colors">{s.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-tight line-clamp-1 hidden sm:block">{s.desc.split("—")[0].split(".")[0].trim()}</p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            <p className="mt-5 text-center text-xs sm:text-sm text-muted-foreground">
              <Flame className="inline w-3.5 h-3.5 text-orange-500 mr-1 -mt-0.5" />
              We also service and replace <strong>water heaters</strong> — ask when you call.
            </p>

            {/* Brands strip */}
            <div className="mt-8 rounded-2xl border bg-muted/40 px-5 py-5 sm:px-8 sm:py-6">
              <p className="text-xs font-bold uppercase tracking-widest text-center text-muted-foreground mb-4">
                Equipment We Install &amp; Service
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 mb-4">
                {["Goodman", "Bosch", "Photon AC"].map((brand) => (
                  <span
                    key={brand}
                    className="rounded-lg border bg-card px-4 py-2 text-sm font-semibold shadow-sm"
                  >
                    {brand}
                  </span>
                ))}
                <span className="rounded-lg border border-dashed bg-card px-4 py-2 text-sm text-muted-foreground">
                  + Most major brands
                </span>
              </div>
              <p className="text-center text-xs sm:text-sm text-muted-foreground">
                We'll service <strong>any make or brand</strong> of system.
              </p>
            </div>
          </div>
        </section>

        {/* ─── VAN / ABOUT STRIP ─────────────────────────────── */}
        <section className="py-8 sm:py-16 bg-muted/30" data-testid="section-about-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center overflow-hidden">
              <Carousel
                opts={{ loop: true }}
                setApi={setCarouselApi}
                className="w-full min-w-0"
                onMouseEnter={() => setAutoplayPaused(true)}
                onMouseLeave={() => setAutoplayPaused(false)}
                onTouchStart={() => setAutoplayPaused(true)}
                data-testid="carousel-job-photos"
              >
                <CarouselContent>
                  {jobPhotos.map((photo, i) => (
                    <CarouselItem key={i}>
                      <img
                        src={photo.src}
                        alt={photo.alt}
                        title={photo.alt}
                        className="rounded-xl object-cover w-full aspect-[4/3] max-h-56 sm:max-h-72 lg:max-h-none bg-muted"
                        loading={i < 5 ? "eager" : "lazy"}
                        decoding="async"
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <div className="absolute inset-y-0 left-3 z-10 flex items-center">
                  <CarouselPrevious className="static translate-x-0 translate-y-0 rotate-0 bg-white/80 hover:bg-white text-black border-none shadow-md" />
                </div>
                <div className="absolute inset-y-0 right-3 z-10 flex items-center">
                  <CarouselNext className="static translate-x-0 translate-y-0 rotate-0 bg-white/80 hover:bg-white text-black border-none shadow-md" />
                </div>
              </Carousel>
              <div className="min-w-0">
                <Badge className="mb-4">About Caribbean Breeze</Badge>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 leading-tight">
                  Family Owned.<br />
                  <span className="text-primary">Done Right.</span>
                </h2>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-4">
                  Caribbean Breeze HVAC was founded in 2017 by a technician with
                  years of hands-on service experience — a company built to do the
                  work right, without the upsell pressure that comes with the
                  bigger outfits.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
                  We're family owned and operated. When you call, you're talking to
                  the people who do the work — and we take every job personally,
                  because it's our name on the van.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {[
                    { label: "Founded", value: "2017" },
                    { label: "Type", value: "Family Owned" },
                    { label: "Warranty", value: "2-Year Labor" },
                    { label: "Service", value: "Emergency Available" },
                  ].map((item) => (
                    <div key={item.label} className="bg-card rounded-lg border p-3.5 text-center">
                      <p className="text-xs text-muted-foreground mb-0.5">{item.label}</p>
                      <p className="font-bold text-sm text-primary">{item.value}</p>
                    </div>
                  ))}
                </div>
                <Link href="/about">
                  <Button variant="outline" className="gap-2">
                    Our Full Story
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ─── HOW IT WORKS ──────────────────────────────────── */}
        <section className="py-8 sm:py-16 lg:py-20" data-testid="section-how-it-works">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6 sm:mb-10">
              <Badge className="mb-3">The Process</Badge>
              <h2 className="text-2xl sm:text-3xl font-bold mb-3">What Happens When You Call</h2>
              <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
                No runaround. We keep it simple.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-4">
              {[
                { step: "1", title: "You Call or Text", body: "Reach us directly at (602) 777-0278. Tell us what's going on with your system." },
                { step: "2", title: "We Schedule", body: "We set a time that works for you — same day when possible, next day in most cases." },
                { step: "3", title: "We Diagnose Honestly", body: "We figure out what's wrong and tell you exactly what it needs. No inflated estimates." },
                { step: "4", title: "Fixed & Backed", body: "We do the work and stand behind it. Every job comes with a 2-year labor warranty." },
              ].map((item) => (
                <div key={item.step} className="text-center relative">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-2 sm:mb-4 text-sm sm:text-lg font-bold shadow-md">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-xs sm:text-base mb-1 sm:mb-2">{item.title}</h3>
                  <p className="text-[11px] sm:text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── SERVICE AREA ──────────────────────────────────── */}
        <section
          className="relative min-h-[200px] sm:min-h-[320px] flex items-center"
          data-testid="section-service-area"
        >
          <img
            src={job4}
            alt="Caribbean Breeze van in the West Valley"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/65" />
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white py-10 sm:py-16">
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-sky-300 mb-2">
              West Valley Based
            </p>
            <h2 className="text-xl sm:text-4xl font-bold mb-3">
              Surprise, AZ &amp; Surrounding Areas
            </h2>
            <p className="text-sm sm:text-base text-white/80 max-w-xl mx-auto mb-3">
              Surprise · Peoria · Glendale · Phoenix · Avondale · Goodyear · Buckeye · Litchfield Park
            </p>
            <p className="text-xs text-white/60 mb-6">Not sure if we cover your zip code? Just call.</p>
            <a href="tel:6027770278" data-testid="button-service-area-call">
              <Button className="bg-amber-400 hover:bg-amber-500 text-black font-bold px-6 sm:px-8 sm:h-12 sm:text-base">
                <Phone className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                (602) 777-0278
              </Button>
            </a>
          </div>
        </section>

        {/* ─── BRANDS ────────────────────────────────────────── */}
        <section className="py-6 sm:py-14 bg-muted/20" data-testid="section-brands">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 sm:mb-6">
              Equipment We Install &amp; Service
            </p>
            <div className="flex flex-wrap justify-center items-center gap-2.5 sm:gap-10">
              {["Goodman", "Bosch", "Photon AC"].map((brand) => (
                <div
                  key={brand}
                  className="px-3.5 py-1.5 sm:px-6 sm:py-3 rounded-lg border border-border bg-card font-bold text-sm sm:text-lg text-foreground/80 hover:text-primary transition-colors"
                >
                  {brand}
                </div>
              ))}
              <div className="px-3.5 py-1.5 sm:px-6 sm:py-3 rounded-lg border border-dashed border-border text-xs sm:text-sm text-muted-foreground">
                + Most major brands serviced
              </div>
            </div>
          </div>
        </section>

        {/* ─── PAYMENT METHODS ───────────────────────────────── */}
        <section className="py-8 bg-card border-y" data-testid="section-payment">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">We Accept</p>
              {["Cash", "Zelle", "PayPal", "Credit Card"].map((method) => (
                <div key={method} className="flex items-center gap-1.5 text-sm font-medium text-foreground/80">
                  <CreditCard className="w-4 h-4 text-primary" />
                  {method}
                </div>
              ))}
              <Link href="/financing">
                <span className="text-sm font-semibold text-primary hover:underline cursor-pointer flex items-center gap-1">
                  Financing available <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* ─── SOLAR AC DIFFERENTIATOR ───────────────────────── */}
        <section className="py-8 sm:py-16 lg:py-20" data-testid="section-solar-showcase">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
              <div>
                <p className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-amber-700 mb-3">
                  Something Different
                </p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 leading-tight">
                  Run Your AC on Sunlight
                </h2>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-4">
                  Solar-powered AC isn't a gimmick — it's a proven system that connects
                  a high-efficiency mini-split directly to solar panels. During the day,
                  your cooling costs near zero. At night or on cloudy days, it switches
                  to the grid automatically. No batteries needed.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
                  We're an authorized{" "}
                  <a
                    href="https://photonac.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline hover:no-underline"
                  >
                    Photon AC
                  </a>{" "}
                  dealer — mini-splits, multi-head systems, and ducted solar replacements.
                  Real equipment, full warranty, installed by us.
                </p>
                <div className="flex items-center gap-3 mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <img
                    src={dealerBadge}
                    alt="Authorized Photon AC Dealer"
                    className="h-12 w-12 object-contain flex-shrink-0"
                    loading="lazy"
                  />
                  <div>
                    <p className="font-bold text-sm text-amber-900">Authorized Photon AC Dealer</p>
                    <p className="text-xs text-amber-700">Genuine equipment. Full 10-year manufacturer warranty.</p>
                  </div>
                </div>
                <Link href="/solar-powered-ac">
                  <Button className="gap-2">
                    See the Solar Systems
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl bg-white p-4 sm:p-6">
                <img
                  src={photonSystemImg}
                  srcSet={`${photonSystemImg640} 640w, ${photonSystemImg} 1327w`}
                  sizes="(min-width: 1024px) 40vw, (min-width: 768px) 50vw, 100vw"
                  alt="Photon AC solar-powered mini-split complete system — indoor unit, solar panels, outdoor condenser, mounting hardware, and smart controls"
                  className="w-full h-full object-contain"
                  width={1327}
                  height={1186}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ─── REVIEWS PLACEHOLDER (Phase 2) ────────────────── */}
        {/*
        TODO-REVIEWS: Populate this section with the dealer's verified
        Google reviews in Phase 2. Do NOT publish with placeholder reviews.
        */}

        {/* ─── CTA BLOCK ─────────────────────────────────────── */}
        <section className="py-12 sm:py-20 bg-primary text-primary-foreground" data-testid="section-cta">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
              <div className="lg:col-span-2">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 leading-tight">
                  Ready to Schedule?<br />Let's Talk.
                </h2>
                <p className="text-sm sm:text-base text-white/85 mb-6">
                  Call us for a free estimate on any job — installation,
                  repair, commercial, or maintenance. We respond fast.
                </p>
                <a
                  href="tel:6027770278"
                  className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-black font-bold px-6 py-3 rounded-lg transition-colors text-base"
                  data-testid="button-cta-call"
                >
                  <Phone className="w-5 h-5" />
                  (602) 777-0278
                </a>
                <p className="mt-3 text-xs text-white/75">
                  Mon–Fri 8am–5pm · Emergency service available
                </p>
              </div>
              <div className="lg:col-span-3 bg-white/10 backdrop-blur-sm rounded-xl p-5 sm:p-6 border border-white/20">
                <p className="font-bold text-base mb-4">Get a Free Estimate</p>
                <ContactForm variant="dark" />
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
